import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { GraduationCap, Download, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Gradebook() {
    const { user } = useAuth();
    const [gradebook, setGradebook] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchGradebook = async () => {
            try {
                const response = await axios.get(`${API}/gradebook`);
                setGradebook(response.data);
            } catch (error) {
                toast.error('Failed to load gradebook');
            } finally {
                setLoading(false);
            }
        };
        fetchGradebook();
    }, []);

    const isInstructor = user.role === 'instructor' || user.role === 'admin';

    const exportToCSV = () => {
        let csv = '';
        if (isInstructor) {
            csv = 'Course,Student,Assignment,Grade,Max Score\n';
            gradebook.forEach(entry => {
                entry.grades.forEach(g => {
                    csv += `"${entry.course_title}","${entry.student_name}","${g.assignment_title}",${g.grade ?? 'N/A'},${g.max_score}\n`;
                });
            });
        } else {
            csv = 'Course,Assignment,Grade,Max Score\n';
            gradebook.forEach(entry => {
                entry.grades.forEach(g => {
                    csv += `"${entry.course_title}","${g.assignment_title}",${g.grade ?? 'N/A'},${g.max_score}\n`;
                });
            });
        }
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'gradebook.csv';
        a.click();
        toast.success('Exported!');
    };

    if (loading) {
        return <div className="p-8"><div className="h-64 skeleton rounded-2xl" /></div>;
    }

    // ── Instructor view: students grouped by course ──
    if (isInstructor) {
        const byCourse = gradebook.reduce((acc, item) => {
            if (!acc[item.course_title]) acc[item.course_title] = [];
            acc[item.course_title].push(item);
            return acc;
        }, {});

        return (
            <div className="space-y-6" data-testid="gradebook-page">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">Gradebook</h1>
                        <p className="text-slate-600">Student grades by course</p>
                    </div>
                    <Button onClick={exportToCSV} variant="outline" className="rounded-full" data-testid="export-btn">
                        <Download className="w-4 h-4 mr-2" /> Export
                    </Button>
                </div>

                {Object.keys(byCourse).length === 0 ? (
                    <div className="bg-white rounded-2xl border p-12 text-center">
                        <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">No student grades yet</p>
                        <p className="text-sm text-slate-400 mt-1">Grades appear once students submit and you grade their work</p>
                    </div>
                ) : (
                    Object.entries(byCourse).map(([courseTitle, students]) => (
                        <div key={courseTitle} className="bg-white rounded-2xl border overflow-hidden">
                            <div className="p-4 bg-[#0F2A44] text-white flex items-center gap-3">
                                <BookOpen className="w-5 h-5 text-[#2F8FCB]" />
                                <h3 className="font-semibold">{courseTitle}</h3>
                                <span className="text-white/50 text-sm ml-auto">{students.length} student{students.length !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50">
                                        <tr className="border-b">
                                            <th className="text-left py-2 px-4">Student</th>
                                            <th className="text-left py-2 px-4">Assignments</th>
                                            <th className="text-right py-2 px-4">Total</th>
                                            <th className="text-right py-2 px-4">%</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {students.map((s, i) => (
                                            <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                                                <td className="py-3 px-4 font-medium">{s.student_name}</td>
                                                <td className="py-3 px-4">
                                                    <div className="flex flex-wrap gap-1">
                                                        {s.grades.map((g, j) => (
                                                            <span key={j} className={`px-2 py-0.5 rounded text-xs ${g.grade !== null ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                                                {g.assignment_title}: {g.grade !== null ? `${g.grade}/${g.max_score}` : 'Pending'}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4 text-right font-mono">
                                                    {(() => { const graded = s.grades.filter(g => g.grade !== null); return `${graded.reduce((a,g) => a+g.grade, 0)}/${graded.reduce((a,g) => a+g.max_score, 0)}`; })()}
                                                </td>
                                                <td className="py-3 px-4 text-right">
                                                    {(() => {
                                                        const graded = s.grades.filter(g => g.grade !== null);
                                                        const pct = graded.length > 0 ? Math.round((graded.reduce((a,g) => a+g.grade, 0) / graded.reduce((a,g) => a+g.max_score, 0)) * 100) : 0;
                                                        return (
                                                            <div>
                                                                <span className={`font-bold ${pct >= 75 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{pct}%</span>
                                                                <p className="text-[10px] text-slate-400">{graded.length}/{s.grades.length} graded</p>
                                                            </div>
                                                        );
                                                    })()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))
                )}
            </div>
        );
    }

    // ── Student view: existing rendering ──
    return (
        <div className="space-y-6" data-testid="gradebook-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">Gradebook</h1>
                    <p className="text-slate-600">Track academic progress</p>
                </div>
                <Button onClick={exportToCSV} variant="outline" className="rounded-full" data-testid="export-btn">
                    <Download className="w-4 h-4 mr-2" /> Export
                </Button>
            </div>

            {gradebook.length === 0 ? (
                <div className="bg-white rounded-2xl border p-12 text-center">
                    <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No grades yet</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {gradebook.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-2xl border overflow-hidden">
                            <div className="p-4 bg-[#0F2A44] text-white">
                                <h3 className="font-semibold">{item.course_title}</h3>
                                <div className="flex items-center gap-4 mt-2">
                                    {(() => {
                                        const graded = item.grades.filter(g => g.grade !== null);
                                        const pct = graded.length > 0 ? Math.round((graded.reduce((a,g) => a+g.grade, 0) / graded.reduce((a,g) => a+g.max_score, 0)) * 100) : 0;
                                        return (
                                            <>
                                                <Progress value={pct} className="flex-1 h-2" />
                                                <span className="font-bold">{pct}%</span>
                                                <span className="text-white/50 text-xs">{graded.length}/{item.grades.length} graded</span>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                            <div className="p-4">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b">
                                            <th className="text-left py-2">Assignment</th>
                                            <th className="text-right py-2">Grade</th>
                                            <th className="text-right py-2">Max</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {item.grades.map((g, i) => (
                                            <tr key={i} className="border-b last:border-0">
                                                <td className="py-2">{g.assignment_title}</td>
                                                <td className="py-2 text-right font-mono">
                                                    {g.grade !== null ? g.grade : <span className="text-xs text-slate-400 font-sans italic">Awaiting Grade</span>}
                                                </td>
                                                <td className="py-2 text-right font-mono text-slate-400">{g.max_score}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
